import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'light'
  
  const saved = localStorage.getItem('attendance_theme') as Theme | null
  if (saved === 'dark' || saved === 'light') {
    return saved
  }

  // Check Telegram WebApp colorScheme
  try {
    const tgScheme = (window as any).Telegram?.WebApp?.colorScheme
    if (tgScheme === 'dark' || tgScheme === 'light') {
      return tgScheme
    }
  } catch {}

  // Check system prefers-color-scheme
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark'
  }

  return 'light'
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme)

  useEffect(() => {
    const root = document.documentElement
    if (theme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
    localStorage.setItem('attendance_theme', theme)
  }, [theme])

  // Listen to Telegram theme changes if inside Telegram
  useEffect(() => {
    const handleTgThemeChange = () => {
      try {
        const tgScheme = (window as any).Telegram?.WebApp?.colorScheme
        if (tgScheme === 'dark' || tgScheme === 'light') {
          // If user hasn't explicitly set localStorage override
          const saved = localStorage.getItem('attendance_theme')
          if (!saved) {
            setThemeState(tgScheme)
          }
        }
      } catch {}
    }

    try {
      (window as any).Telegram?.WebApp?.onEvent?.('themeChanged', handleTgThemeChange)
    } catch {}

    return () => {
      try {
        (window as any).Telegram?.WebApp?.offEvent?.('themeChanged', handleTgThemeChange)
      } catch {}
    }
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeState(prev => (prev === 'light' ? 'dark' : 'light'))
  }, [])

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme)
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
